import type { ReactNode } from 'react';
import styled from 'styled-components';
import { Select, Typography } from 'antd';
import { FilterChip } from './FilterChip';
import { useTranslation } from 'react-i18next';

type FilterContent = {
  key: string;
  label: string;
  onClose: () => void;
};

type Props = {
  title: string;
  icon: ReactNode;
  contents: FilterContent[];
  relation: 'and' | 'or';
  onChangeRelation?: (relation: 'and' | 'or') => void;
};

const BRACKET_STYLE_THRESHOLD = 25;

const FilterContentGroup = ({
  title,
  icon,
  contents,
  relation,
  onChangeRelation,
}: Props): ReactNode => {
  const { t } = useTranslation();

  const contentLength = contents.reduce(
    (sum: number, content: FilterContent): number => {
      // フィルター内容の文字数と閉じるボタンなどのスペースを考慮して、全体の長さを計算する
      return sum + content.label.length + 3;
    },
    0
  );

  if (contents.length === 0) {
    return null;
  }
  return (
    <GridContainer>
      <FilterTitle>
        {icon}
        <Typography.Text>{title}</Typography.Text>
      </FilterTitle>
      {contentLength >= BRACKET_STYLE_THRESHOLD ? (
        <RelationBracket>
          {onChangeRelation && contents.length > 1 ? (
            <RelationSelector
              value={relation}
              onChange={onChangeRelation}
              options={[
                { value: 'or', label: t('or') },
                { value: 'and', label: t('and') },
              ]}
              dropdownMatchSelectWidth={false}
              dropdownStyle={{ textTransform: 'uppercase' }}
              placement="bottomRight"
            />
          ) : (
            t(relation)
          )}
        </RelationBracket>
      ) : (
        <div />
      )}
      <FilterContent>
        {contents.map((content: FilterContent, index: number): ReactNode => (
          <FilterChip
            key={content.key}
            closable
            color="blue"
            onClose={content.onClose}
            relation={relation}
            showsRelation={contentLength < BRACKET_STYLE_THRESHOLD}
            isLast={index === contents.length - 1}
            onChangeRelation={onChangeRelation}
          >
            {content.label}
          </FilterChip>
        ))}
      </FilterContent>
    </GridContainer>
  );
};

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 5rem 3rem 1fr;
  align-items: center;
  gap: 4px;
`;

const FilterTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
`;

const FilterContent = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
`;

const RelationBracket = styled.div`
  height: 100%;
  font-size: 10px;
  opacity: 0.4;
  font-weight: bold;
  white-space: nowrap;
  text-transform: uppercase;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  &::before {
    content: '';
    flex-grow: 1;
    display: block;
    width: 8px;
    height: 8px;
    border-top: 2px solid #c8c8c8;
    border-left: 2px solid #c8c8c8;
  }
  &::after {
    content: '';
    flex-grow: 1;
    display: block;
    width: 8px;
    height: 8px;
    border-left: 2px solid #c8c8c8;
    border-bottom: 2px solid #c8c8c8;
  }
`;

const RelationSelector = styled(Select<'and' | 'or'>)`
  &.ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
    height: 22px;
    padding: 0 4px;
    align-items: center;
  }

  &.ant-select-single.ant-select-show-arrow .ant-select-selection-item {
    padding-right: 12px;
  }

  &.ant-select-single .ant-select-selector .ant-select-selection-item {
    line-height: 1;
  }

  .ant-select-selection-item {
    font-size: 10px;
    font-weight: bold;
    text-transform: uppercase;
  }

  .ant-select-arrow {
    top: calc(50% + 1px);
    right: 4px;
  }
`;

export { FilterContentGroup };
